'use client';
import { PaginationV2 } from '@pickleballinc/react-ui';
import { RenderAd } from '@pickleballinc/ui/components/advertisements/render-ad';
import { NoDataFound } from '@pickleballinc/ui/components/global/NoDataFound';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/navigation';
import { Fragment, useMemo } from 'react';

import { PlayerCard } from '@/modules/rankings/PlayerCard/PlayerCard';
import { TableOptions } from '@/modules/rankings/TableOptions';
import { ZONE_IDS_PB } from '@/types/ads.types';
import { PLACEMENT_IDS_PB } from '@/types/freestar.pb.types';
import { PlayersRankingsData } from '@/types/ranking.types';
import { IS_PRODUCTION } from '@/utils/helpers/constants';

interface PlayerTableProps {
  rankings: PlayersRankingsData[];
  totalRecords: number;
  ipAddress: string;
}

export const PlayerTable = ({ rankings, totalRecords, ipAddress }: PlayerTableProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const page = searchParams?.get('page') ? Number(searchParams?.get('page')) : 1;

  const sortColumn = searchParams?.get('sortBy') ?? 'rank';
  const sortDirection = searchParams?.get('sortDir') ?? 'up';
  const sortConfig = useMemo(
    () => ({ column: sortColumn, direction: sortDirection }),
    [sortColumn, sortDirection]
  );

  const handlePageChange = (newPage: number) => {
    const newSearchParams = new URLSearchParams(searchParams || '');
    newSearchParams.set('page', newPage.toString());
    router.replace(`?${newSearchParams.toString()}`);
  };

  const handleSort = (column: string) => {
    const nextDirection =
      sortConfig.column === column && sortConfig.direction === 'up' ? 'down' : 'up';
    const newSearchParams = new URLSearchParams(searchParams || '');
    newSearchParams.set('sortBy', column);
    newSearchParams.set('sortDir', nextDirection);
    router.replace(`?${newSearchParams.toString()}`, { scroll: false });
  };

  const sortedRankings = useMemo(() => {
    const sortedData = rankings && rankings?.length > 0 ? [...rankings] : [];

    const getValue = (player: PlayersRankingsData, column: string) => {
      switch (column) {
        case 'rank':
          return Number(player.ranking || 0);
        case 'player':
          return player.playerFullName || '';
        case 'country':
          return player.playerCountryAbbreviation || '';
        case 'age':
          return player.age;
        case 'played':
          return player.totalEventsPlayed;
        case 'points':
          return player.points;
        case 'currentTournament':
          return player.liveTournamentTitle || '';
        case 'livePoints':
          return player.livePoints;
        case 'officialPoints':
          return player.points;
        case 'plusMinus':
          return player.livePointsChange;
        case 'nextMaxPoints':
          return player.liveNextPoints;
        default:
          return 0;
      }
    };

    sortedData.sort((a, b) => {
      const aValue = getValue(a, sortConfig.column);
      const bValue = getValue(b, sortConfig.column);

      // Handle string sorting with proper case-insensitive comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue, undefined, {
          sensitivity: 'base',
        });
        return sortConfig.direction === 'up' ? comparison : -comparison;
      }

      // Handle numerical sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'up' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return sortedData;
  }, [rankings, sortConfig]);

  return (
    <div className='ml-0 hidden w-full max-w-screen-xl bg-white px-4 2xl:px-0 sm:flex'>
      <div className='ml-0 w-full max-w-screen-xl'>
        {sortedRankings.length > 0 && (
          <TableOptions handleSort={handleSort} sortConfig={sortConfig} />
        )}
        {sortedRankings.length > 0 ? (
          sortedRankings.map((player, index) => (
            <Fragment key={player.playerUuid}>
              <PlayerCard
                uuid={player.playerUuid}
                slug={player.playerSlug}
                rank={player.ranking}
                fullName={player.playerFullName}
                country={player.playerCountryAbbreviation}
                flagUrl={player.countryImage}
                age={player.age}
                played={player.totalEventsPlayed}
                doublesPlayed={player.totalEventsPlayedDoubles}
                mixedPlayed={player.totalEventsPlayedMixed}
                singlesPlayed={player.totalEventsPlayedSingles}
                gender={player.gender}
                points={player.points}
                livePoints={player.livePoints}
                avatarUrl={player.profileImage}
                change={player.liveRankingChange}
                big={index < 10 && page === 1}
                currentTournamentUrl={player.liveTournamentLogo}
                currentTournamentText={player.liveTournamentTitle}
                officialPoints={player.points}
                plusMinus={player.livePointsChange}
                nextPoints={player.liveNextPoints}
                maxPoints={player.liveMaxPoints}
              />

              {(index - 9) % 30 === 0 && index !== 0 && (
                <RenderAd
                  placementId={PLACEMENT_IDS_PB.incontent_728_reusable}
                  zoneId={ZONE_IDS_PB.PROG_RANKINGS_BANNER}
                  pageId={100013}
                  place={Math.floor((index - 9) / 30)}
                  ipAddress={ipAddress}
                  isProd={IS_PRODUCTION}
                  containerClassname='mx-0.5 my-2 sm:container sm:mx-auto'
                />
              )}
            </Fragment>
          ))
        ) : (
          <div className='my-4'>
            <NoDataFound
              title='No players found'
              subtitle='Try adjusting your search or browsing our categories.'
            />
          </div>
        )}

        <div className='my-6'>
          <PaginationV2
            totalNumberOfPages={Math.ceil(totalRecords / 100)}
            currentPage={page}
            onPreviousClick={() => handlePageChange(page - 1)}
            onNextClick={() => handlePageChange(page + 1)}
            onPageClick={(newPage) => handlePageChange(newPage)}
          />
        </div>
      </div>
    </div>
  );
};
